import { useCallback, useEffect, useMemo, useState } from 'react';

import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Alert, AlertTitle, Dialog, Snackbar } from '@mui/material';
import type { DeepPartial } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import type { CreateBuyerLeadMutationVariables } from '@graphql/__generated__/graphql';
import { CREATE_BUYER_LEAD } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import {
  type BuyerLeadStage,
  buildOriginationActivity,
  getBuyerLeadErrorMessage,
  getBuyerLeadStageId,
  getBuyerLeadStageOptions,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { DialogStickyHeader } from '@modules/_shared/components/DialogStickyHeader';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useLocale } from '@shared/hooks/locale';

type BuyerLeadAddModalProps = {
  destinationStageId?: string | null;
  stages?: BuyerLeadStage[];
  defaultStageName?: string;
  opened: boolean;
  onClose: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
  mlsShareId?: string;
  /** Skip origination activity insert — required for cross-tenant MLS lots
   *  where the buyer_lead's tenant_id is rewritten by trigger to the lot
   *  owner's tenant, making the composite FK on activities unsatisfiable. */
  skipActivities?: boolean;
} & (
  | {
      lotId: string;
      developmentId?: never;
    }
  | {
      developmentId: string;
      lotId?: never;
    }
);

type NewBuyerLeadFormData = { lotId: string; stageId?: string } & (
  | {
      user: string;
    }
  | {
      firstName: string;
      lastName: string;
      email: string | null;
      phone: string | null;
    }
);

type DefaultUserValue = {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
};

export const BuyerLeadAddModal: React.FC<BuyerLeadAddModalProps> = props => {
  const {
    destinationStageId = null,
    stages,
    defaultStageName = 'interested',
    opened,
    onClose,
    refetchQueries,
    mlsShareId,
    skipActivities,
  } = props;
  const showStageSelect = stages != null && stages.length > 0;
  const { t } = useLocale();
  const { me } = useAppData();
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [defaultUserValue, setDefaultUserValue] =
    useState<DefaultUserValue | null>(null);
  const [displaySuccessMessage, setDisplaySuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lotId = 'lotId' in props ? props.lotId : undefined;
  const developmentId =
    'developmentId' in props ? props.developmentId : undefined;

  const [insertBuyerLead] = useMutation(CREATE_BUYER_LEAD, {
    onCompleted: data => {
      if (data.insert_buyer_leads_one != null) {
        setDisplaySuccessMessage(true);
        onClose();
        emitFeedRefresh();
      }
    },
    onError: error => setErrorMessage(getBuyerLeadErrorMessage(error, t)),
    refetchQueries,
  });

  useEffect(() => {
    if (opened) {
      setIsCreatingUser(false);
      setDefaultUserValue(null);
      setErrorMessage(null);
    }
  }, [opened]);

  const formDefinition = useCallback<FormDefinitionType<NewBuyerLeadFormData>>(
    ({ t }) => {
      const form: FormFieldDefinitionType<NewBuyerLeadFormData>[] =
        isCreatingUser
          ? [
              {
                type: 'text',
                required: true,
                name: 'firstName',
                label: t('First name'),
              },
              {
                type: 'text',
                required: true,
                name: 'lastName',
                label: t('Last name'),
              },
              {
                type: 'text',
                name: 'email',
                label: t('Email'),
              },
              {
                type: 'phone_number',
                name: 'phone',
                label: t('Phone'),
              },
            ]
          : [
              {
                type: 'user',
                name: 'user',
                label: t('Buyer'),
                required: true,
                autoFocus: true,
                createUserSelected: userData => {
                  setIsCreatingUser(true);

                  const newDefaultValues: {
                    firstName?: string;
                    lastName?: string;
                    email?: string | null;
                    phone?: string | null;
                  } = { email: userData.email };

                  if (userData.first_name != null) {
                    newDefaultValues.firstName = userData.first_name;
                  }

                  if (userData.last_name != null) {
                    newDefaultValues.lastName = userData.last_name;
                  }

                  setDefaultUserValue(newDefaultValues);
                },
                gridProps: { md: 12 },
              },
            ];

      if (showStageSelect) {
        form.push({
          type: 'select',
          name: 'stageId',
          label: t('Pipeline step'),
          required: true,
          options: () => getBuyerLeadStageOptions(stages),
          gridProps: { md: 12 },
        });
      }

      if (developmentId != null) {
        form.push({
          type: 'lot',
          name: 'lotId',
          label: t('Listing'),
          required: true,
          limitToDevelopment: developmentId,
          gridProps: { md: 12 },
        });
      }

      return form;
    },
    [isCreatingUser, developmentId, showStageSelect, stages],
  );

  const defaultValues = useMemo<DeepPartial<NewBuyerLeadFormData>>(() => {
    const defaultStageId = showStageSelect
      ? getBuyerLeadStageId(stages, defaultStageName) ??
        getBuyerLeadStageId(stages, 'interested') ??
        stages?.[0]?.id ??
        null
      : null;
    return {
      ...(defaultUserValue ?? {}),
      lotId,
      ...(defaultStageId != null && { stageId: defaultStageId }),
    };
  }, [defaultUserValue, lotId, defaultStageName, showStageSelect, stages]);

  return (
    <>
      <Dialog
        open={opened}
        fullWidth
        maxWidth={isCreatingUser ? 'sm' : 'xs'}
        onClose={onClose}
        slots={{
          transition: SlideUpTransition,
        }}
        sx={{
          '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
        }}
      >
        <DialogStickyHeader title={t('New buyer lead')} onClose={onClose} />
        <RaForm
          formDefinition={formDefinition}
          onCancel={onClose}
          defaultValues={defaultValues}
          onSubmit={data => {
            const stageId = showStageSelect ? data.stageId : destinationStageId;
            if (stageId == null) {
              return Promise.reject();
            }
            const buyerLeadUserId = 'user' in data ? data.user : uuidv4();

            const variables: CreateBuyerLeadMutationVariables = {
              buyerLead: {
                lot_id: data.lotId,
                stage_id: stageId,
                source: 'manual',
                // Cross-tenant MLS: activity.tenant_id is set to the buyer agent's
                // tenant by Hasura but buyer_lead.tenant_id is rewritten to the lot
                // owner's tenant by trigger — violating the composite FK on activities.
                // skipActivities must be true for all cross-tenant MLS lots.
                ...(skipActivities !== true
                  ? {
                      activities: buildOriginationActivity(
                        buyerLeadUserId,
                        me?.is_admin ? me?.id : undefined,
                      ),
                    }
                  : {}),
                ...(mlsShareId != null ? { mls_share_id: mlsShareId } : {}),
              },
            };

            if ('user' in data) {
              variables.buyerLead.user_id = data.user;
            } else {
              variables.buyerLead.user = {
                data: {
                  id: buyerLeadUserId,
                  first_name: data.firstName,
                  last_name: data.lastName,
                  emails:
                    data.email != null
                      ? { data: [{ email: data.email }] }
                      : undefined,
                  phone_numbers:
                    data.phone != null
                      ? { data: [{ number: data.phone }] }
                      : undefined,
                },
              };
            }

            return insertBuyerLead({ variables });
          }}
          actionButtonsSlotProps={{
            container: {
              elevation: 0,
              sx: theme => ({
                borderTop: `1px solid ${(theme.vars ?? theme).palette.divider}`,
              }),
            },
          }}
        >
          {errorMessage != null && (
            <Alert severity="error" sx={{ m: 2 }}>
              <AlertTitle>
                {t('An error occured while creating the buyer lead.')}
              </AlertTitle>
              {errorMessage}
            </Alert>
          )}
        </RaForm>
      </Dialog>
      <Snackbar
        open={displaySuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
        onClose={() => setDisplaySuccessMessage(false)}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {t('Buyer lead created successfully!')}
        </Alert>
      </Snackbar>
    </>
  );
};
